import React, { createContext, useContext, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface RegistrationContextType {
  isRegistering: boolean;
  selectedTournamentId: string | null;
  startRegistration: (tournamentId: string) => void;
  completeRegistration: () => void;
  cancelRegistration: () => void;
  registeredMemberId: string | null;
  isNewMember: boolean;
  setRegistrationResult: (memberId: string, isNew: boolean) => void;
}

const RegistrationContext = createContext<RegistrationContextType | undefined>(undefined);

export function useRegistration() {
  const context = useContext(RegistrationContext);
  if (context === undefined) {
    throw new Error('useRegistration must be used within a RegistrationProvider');
  }
  return context;
}

interface RegistrationProviderProps {
  children: React.ReactNode;
}

export function RegistrationProvider({ children }: RegistrationProviderProps) {
  const [isRegistering, setIsRegistering] = useState(false);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(null);
  const [registeredMemberId, setRegisteredMemberId] = useState<string | null>(null);
  const [isNewMember, setIsNewMember] = useState(false);

  const startRegistration = (tournamentId: string) => {
    setSelectedTournamentId(tournamentId);
    setIsRegistering(true);
  };

  const completeRegistration = () => {
    setIsRegistering(false);
    setSelectedTournamentId(null);
  };

  const cancelRegistration = () => {
    setIsRegistering(false);
    setSelectedTournamentId(null);
    setRegisteredMemberId(null);
    setIsNewMember(false);
  };

  const setRegistrationResult = (memberId: string, isNew: boolean) => {
    setRegisteredMemberId(memberId);
    setIsNewMember(isNew);
  };

  const value = {
    isRegistering,
    selectedTournamentId,
    startRegistration,
    completeRegistration,
    cancelRegistration,
    registeredMemberId,
    isNewMember,
    setRegistrationResult,
  };

  return (
    <RegistrationContext.Provider value={value}>
      {children(value)}
    </RegistrationContext.Provider>
  );
}